/**
 * Clerk-backed session provider.
 *
 * Clerk loads its script from the network. When that fails — the usual case for a cold start
 * on a dead connection — this provider reports the cached identity instead of blocking, so
 * the roll call stays usable and edits queue for later.
 */

import { useCallback, useMemo, type ReactNode } from 'react';
import { ClerkProvider, useAuth, useUser } from '@clerk/clerk-react';

import { CLERK_APPEARANCE } from './clerk-appearance.js';

import { useStore } from '../data/context.js';
import { SessionContextProvider, SessionStatus, type Session, type SessionContextValue } from './session.js';

/** Bridges Clerk's hooks onto the app's session contract. */
function ClerkSessionBridge({ children }: { children: ReactNode }): ReactNode {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { user } = useUser();
  const store = useStore();
  const cached = store.identity;

  const getBearerToken = useCallback(async (): Promise<string | null> => {
    if (!isLoaded || isSignedIn !== true) return null;
    return getToken();
  }, [isLoaded, isSignedIn, getToken]);

  const endSession = useCallback(async (): Promise<void> => {
    await store.forget();
    await signOut();
  }, [store, signOut]);

  const value = useMemo((): SessionContextValue => {
    const liveSession: Session | null = user === null || user === undefined
      ? null
      : {
        userId: user.id,
        email: (user.primaryEmailAddress?.emailAddress ?? user.emailAddresses[0]?.emailAddress ?? '').toLowerCase(),
      };

    if (!isLoaded) {
      return cached === null
        ? { status: SessionStatus.Loading, session: null, getToken: getBearerToken, signOut: endSession }
        : { status: SessionStatus.Cached, session: cached, getToken: getBearerToken, signOut: endSession };
    }
    if (isSignedIn === true && liveSession !== null) {
      return { status: SessionStatus.Active, session: liveSession, getToken: getBearerToken, signOut: endSession };
    }
    return { status: SessionStatus.SignedOut, session: null, getToken: getBearerToken, signOut: endSession };
  }, [isLoaded, isSignedIn, user, cached, getBearerToken, endSession]);

  return <SessionContextProvider value={value}>{children}</SessionContextProvider>;
}

/** Wraps the app in Clerk and exposes the session contract. */
export function ClerkSession({
  publishableKey,
  children,
}: {
  publishableKey: string;
  children: ReactNode;
}): ReactNode {
  return (
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/" appearance={CLERK_APPEARANCE}>
      <ClerkSessionBridge>{children}</ClerkSessionBridge>
    </ClerkProvider>
  );
}
